import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  Container,
  Form,
  Row,
  Col,
  Spinner,
  Image,
  Alert,
  Badge,
  ProgressBar
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { MotionDiv } from "../../components";
import Title from "../../components/layout/Title";
import TextEditor from "../../components/layout/TextEditor";
import {
  useCreateContentMutation,
  useGetContentMutation,
  useUploadImageMutation,
} from "../../features/apiSlice";
import { getError } from "../../utils/error";
import {
  FaCamera,
  FaLightbulb,
  FaUpload,
  FaUser,
  FaHome,
  FaSearch,
  FaLink,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaTiktok,
  FaTelegram,
  FaGlobe,
  FaTimes,
  FaCheck
} from "react-icons/fa";

export default function WebsiteContent() {
  const navigate = useNavigate();
  const { subdomain } = useParams();

  // API Mutations
  const [uploadImage] = useUploadImageMutation();
  const [getContent, { isLoading: getLoading }] = useGetContentMutation();
  const [createContent, { isLoading: createLoading }] = useCreateContentMutation();

  // State for all form fields
  const [formData, setFormData] = useState({
    // Brand Identity
    logo: "",
    metaTitle: "",
    metaDescription: "",
    
    // Home Page
    bannerImage: "",
    homeHeading: "",
    homeTagline: "",
    
    // About Us
    aboutDescription: "",
    aboutImage: "",
    aboutImageAltText: "",
    
    // Insight Section
    insightHeading: "",
    insightSubheading: "",
    insightImage: "",
    insightImageAltText: "",
    
    // Social Links
    socialLinks: {
      facebookUrl: "",
      instagramUrl: "",
      twitterUrl: "",
      linkedinUrl: "",
      tiktokUrl: "",
      telegramUrl: ""
    }
  });

  // INDEPENDENT IMAGE STATES
  const [logoState, setLogoState] = useState({
    preview: "",
    file: null,
    isLoading: false,
    progress: 0
  });

  const [bannerState, setBannerState] = useState({
    preview: "",
    file: null,
    isLoading: false,
    progress: 0
  });

  const [aboutState, setAboutState] = useState({
    preview: "",
    file: null,
    isLoading: false,
    progress: 0
  });

  const [insightState, setInsightState] = useState({
    preview: "",
    file: null,
    isLoading: false,
    progress: 0
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Refs for file inputs
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const aboutInputRef = useRef(null);
  const insightInputRef = useRef(null);

  // Fetch content on component mount
  useEffect(() => {
    if (subdomain) {
      fetchContent();
    }
  }, [subdomain]);

  // Fetch content from API
  const fetchContent = async () => {
    try {
      const data = await getContent(subdomain).unwrap();
      
      setFormData({
        logo: data?.logo || "",
        metaTitle: data?.metaTitle || "",
        metaDescription: data?.metaDescription || "",
        bannerImage: data?.homePage?.bannerImage || "",
        homeHeading: data?.homePage?.heading || "",
        homeTagline: data?.homePage?.tagline || "",
        aboutDescription: data?.aboutUs?.description || "",
        aboutImage: data?.aboutUs?.aboutImage || "",
        aboutImageAltText: data?.aboutUs?.aboutImageAltText || "",
        insightHeading: data?.insightSection?.heading || "",
        insightSubheading: data?.insightSection?.subheading || "",
        insightImage: data?.insightSection?.insightImage || "",
        insightImageAltText: data?.insightSection?.insightImageAltText || "",
        socialLinks: {
          facebookUrl: data?.socialLinks?.facebookUrl || "",
          instagramUrl: data?.socialLinks?.instagramUrl || "",
          twitterUrl: data?.socialLinks?.twitterUrl || "",
          linkedinUrl: data?.socialLinks?.linkedinUrl || "",
          tiktokUrl: data?.socialLinks?.tiktokUrl || "",
          telegramUrl: data?.socialLinks?.telegramUrl || ""
        }
      });

      // Set previews from API data
      setLogoState(prev => ({ ...prev, preview: data?.logo || "" }));
      setBannerState(prev => ({ ...prev, preview: data?.homePage?.bannerImage || "" }));
      setAboutState(prev => ({ ...prev, preview: data?.aboutUs?.aboutImage || "" }));
      setInsightState(prev => ({ ...prev, preview: data?.insightSection?.insightImage || "" }));
      
    } catch (error) {
      toast.error(getError(error));
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTouched(prev => ({ ...prev, [field]: true }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle social link changes
  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  // INDEPENDENT IMAGE UPLOAD HANDLERS
  const handleLogoUpload = async (file) => {
    try {
      setLogoState(prev => ({ ...prev, isLoading: true, progress: 10 }));
      
      const form = new FormData();
      form.append("image", file);
      
      const data = await uploadImage(form).unwrap();
      
      // Update both preview and form data
      setLogoState(prev => ({ ...prev, preview: data?.location, progress: 100 }));
      handleChange('logo', data?.location);
      
      setTimeout(() => {
        setLogoState(prev => ({ ...prev, isLoading: false, progress: 0 }));
      }, 1000);
      
      toast.success("Logo uploaded successfully!");
      
    } catch (error) {
      toast.error(getError(error));
      setLogoState(prev => ({ ...prev, isLoading: false, progress: 0 }));
    }
  };

  const handleBannerUpload = async (file) => {
    try {
      setBannerState(prev => ({ ...prev, isLoading: true, progress: 10 }));
      
      const form = new FormData();
      form.append("image", file);
      
      const data = await uploadImage(form).unwrap();
      
      // Update both preview and form data
      setBannerState(prev => ({ ...prev, preview: data?.location, progress: 100 }));
      handleChange('bannerImage', data?.location);
      
      setTimeout(() => {
        setBannerState(prev => ({ ...prev, isLoading: false, progress: 0 }));
      }, 1000);
      
      toast.success("Banner image uploaded successfully!");
      
    } catch (error) {
      toast.error(getError(error));
      setBannerState(prev => ({ ...prev, isLoading: false, progress: 0 }));
    }
  };

  const handleAboutUpload = async (file) => {
    try {
      setAboutState(prev => ({ ...prev, isLoading: true, progress: 10 }));
      
      const form = new FormData();
      form.append("image", file);
      
      const data = await uploadImage(form).unwrap();
      
      // Update both preview and form data
      setAboutState(prev => ({ ...prev, preview: data?.location, progress: 100 }));
      handleChange('aboutImage', data?.location);
      
      setTimeout(() => {
        setAboutState(prev => ({ ...prev, isLoading: false, progress: 0 }));
      }, 1000);
      
      toast.success("About image uploaded successfully!");
      
    } catch (error) {
      toast.error(getError(error));
      setAboutState(prev => ({ ...prev, isLoading: false, progress: 0 }));
    }
  };

  const handleInsightUpload = async (file) => {
    try {
      setInsightState(prev => ({ ...prev, isLoading: true, progress: 10 }));
      
      const form = new FormData();
      form.append("image", file);
      
      const data = await uploadImage(form).unwrap();
      
      // Update both preview and form data
      setInsightState(prev => ({ ...prev, preview: data?.location, progress: 100 }));
      handleChange('insightImage', data?.location);
      
      setTimeout(() => {
        setInsightState(prev => ({ ...prev, isLoading: false, progress: 0 }));
      }, 1000);
      
      toast.success("Insight image uploaded successfully!");
      
    } catch (error) {
      toast.error(getError(error));
      setInsightState(prev => ({ ...prev, isLoading: false, progress: 0 }));
    }
  };

  // File selection handlers
  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoState(prev => ({ ...prev, file }));
      handleLogoUpload(file);
    }
  };

  const handleBannerSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBannerState(prev => ({ ...prev, file }));
      handleBannerUpload(file);
    }
  };

  const handleAboutSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAboutState(prev => ({ ...prev, file }));
      handleAboutUpload(file);
    }
  };

  const handleInsightSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setInsightState(prev => ({ ...prev, file }));
      handleInsightUpload(file);
    }
  };

  // Remove image handlers
  const handleLogoRemove = () => {
    setLogoState({
      preview: "",
      file: null,
      isLoading: false,
      progress: 0
    });
    handleChange('logo', "");
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    toast.info("Logo removed");
  };

  const handleBannerRemove = () => {
    setBannerState({
      preview: "",
      file: null,
      isLoading: false,
      progress: 0
    });
    handleChange('bannerImage', "");
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
    toast.info("Banner image removed");
  };

  const handleAboutRemove = () => {
    setAboutState({
      preview: "",
      file: null,
      isLoading: false,
      progress: 0
    });
    handleChange('aboutImage', "");
    if (aboutInputRef.current) {
      aboutInputRef.current.value = '';
    }
    toast.info("About image removed");
  };

  const handleInsightRemove = () => {
    setInsightState({
      preview: "",
      file: null,
      isLoading: false,
      progress: 0
    });
    handleChange('insightImage', "");
    if (insightInputRef.current) {
      insightInputRef.current.value = '';
    }
    toast.info("Insight image removed");
  };

  // Trigger file inputs
  const triggerLogoInput = () => {
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
      logoInputRef.current.click();
    }
  };

  const triggerBannerInput = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
      bannerInputRef.current.click();
    }
  };

  const triggerAboutInput = () => {
    if (aboutInputRef.current) {
      aboutInputRef.current.value = '';
      aboutInputRef.current.click();
    }
  };

  const triggerInsightInput = () => {
    if (insightInputRef.current) {
      insightInputRef.current.value = '';
      insightInputRef.current.click();
    }
  };

  // Validate URL format
  const isValidUrl = (url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate URLs
    Object.entries(formData.socialLinks).forEach(([platform, url]) => {
      if (url && !isValidUrl(url)) {
        newErrors[`social_${platform}`] = `Please enter a valid ${platform} URL`;
      }
    });
    
    // Validate required fields
    if (!formData.homeHeading) newErrors.homeHeading = "Home heading is required";
    if (!formData.metaTitle) newErrors.metaTitle = "Meta title is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    try {
      const contentData = {
        logo: formData.logo,
        homePageBannerImage: formData.bannerImage,
        homePageHeading: formData.homeHeading,
        homePageTagline: formData.homeTagline,
        aboutUsDescription: formData.aboutDescription,
        aboutUsImage: formData.aboutImage,
        aboutUsImageAltText: formData.aboutImageAltText,
        insightHeading: formData.insightHeading,
        insightSubheading: formData.insightSubheading,
        insightImage: formData.insightImage,
        insightImageAltText: formData.insightImageAltText,
        ...formData.socialLinks,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        subdomain_reference: subdomain,
      };
      
      const data = await createContent(contentData).unwrap();
      toast.success(data?.message || "Content saved successfully!");
      navigate(-1);
      
    } catch (error) {
      toast.error(getError(error));
    }
  };

  // Social media platform configurations
  const socialPlatforms = [
    { key: 'facebookUrl', icon: FaFacebook, label: 'Facebook', color: '#1877F2', placeholder: 'https://facebook.com/yourpage' },
    { key: 'instagramUrl', icon: FaInstagram, label: 'Instagram', color: '#E4405F', placeholder: 'https://instagram.com/yourprofile' },
    { key: 'twitterUrl', icon: FaTwitter, label: 'Twitter', color: '#1DA1F2', placeholder: 'https://twitter.com/yourhandle' },
    { key: 'linkedinUrl', icon: FaLinkedin, label: 'LinkedIn', color: '#0A66C2', placeholder: 'https://linkedin.com/company/yourcompany' },
    { key: 'tiktokUrl', icon: FaTiktok, label: 'TikTok', color: '#000000', placeholder: 'https://tiktok.com/@yourusername' },
    { key: 'telegramUrl', icon: FaTelegram, label: 'Telegram', color: '#0088CC', placeholder: 'https://t.me/yourchannel' }
  ];

  // Section components for better organization
  const SectionHeader = ({ icon: Icon, title, subtitle, color = "primary" }) => (
    <div className="mb-3">
      <h5 className="d-flex align-items-center gap-2">
        <Icon className={`text-${color}`} />
        {title}
      </h5>
      <p className="text-muted mb-0">{subtitle}</p>
    </div>
  );

  // INDEPENDENT IMAGE UPLOADER COMPONENTS
  const LogoUploader = () => (
    <div className="mb-3">
      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
        <FaCamera size={14} className="text-muted" />
        Logo Image
      </Form.Label>
      
      <input
        ref={logoInputRef}
        type="file"
        onChange={handleLogoSelect}
        style={{ display: "none" }}
        accept="image/*"
      />
      
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <Button
          variant="outline-primary"
          onClick={triggerLogoInput}
          disabled={logoState.isLoading}
          className="d-flex align-items-center gap-2"
        >
          {logoState.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <FaUpload /> {logoState.preview ? 'Change Logo' : 'Upload Logo'}
            </>
          )}
        </Button>
        
        {logoState.progress > 0 && (
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: '120px' }}>
              <ProgressBar 
                now={logoState.progress} 
                label={`${logoState.progress}%`}
                variant={logoState.progress === 100 ? "success" : "primary"}
              />
            </div>
          </div>
        )}
        
        {logoState.preview && (
          <div className="position-relative d-inline-block">
            <Image
              src={logoState.preview}
              alt="Logo Preview"
              style={{
                width: '100px',
                height: '100px',
                objectFit: 'contain',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                backgroundColor: '#f8f9fa'
              }}
              thumbnail
            />
            <Button
              variant="danger"
              size="sm"
              className="position-absolute top-0 end-0 translate-middle"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleLogoRemove}
              disabled={logoState.isLoading}
            >
              <FaTimes />
            </Button>
          </div>
        )}
        
        {logoState.file && (
          <small className="text-muted">
            Selected: {logoState.file.name}
          </small>
        )}
      </div>
      <Form.Text className="text-muted">
        Recommended: PNG or SVG, transparent background, 500x500px
      </Form.Text>
    </div>
  );

  const BannerUploader = () => (
    <div className="mb-3">
      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
        <FaCamera size={14} className="text-muted" />
        Banner Image
      </Form.Label>
      
      <input
        ref={bannerInputRef}
        type="file"
        onChange={handleBannerSelect}
        style={{ display: "none" }}
        accept="image/*"
      />
      
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <Button
          variant="outline-primary"
          onClick={triggerBannerInput}
          disabled={bannerState.isLoading}
          className="d-flex align-items-center gap-2"
        >
          {bannerState.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <FaUpload /> {bannerState.preview ? 'Change Banner' : 'Upload Banner'}
            </>
          )}
        </Button>
        
        {bannerState.progress > 0 && (
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: '120px' }}>
              <ProgressBar 
                now={bannerState.progress} 
                label={`${bannerState.progress}%`}
                variant={bannerState.progress === 100 ? "success" : "primary"}
              />
            </div>
          </div>
        )}
        
        {bannerState.preview && (
          <div className="position-relative d-inline-block">
            <Image
              src={bannerState.preview}
              alt="Banner Preview"
              style={{
                width: '150px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '2px solid #dee2e6'
              }}
              thumbnail
            />
            <Button
              variant="danger"
              size="sm"
              className="position-absolute top-0 end-0 translate-middle"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleBannerRemove}
              disabled={bannerState.isLoading}
            >
              <FaTimes />
            </Button>
          </div>
        )}
        
        {bannerState.file && (
          <small className="text-muted">
            Selected: {bannerState.file.name}
          </small>
        )}
      </div>
      <Form.Text className="text-muted">
        Recommended: JPG or PNG, 1200x600px, landscape orientation
      </Form.Text>
    </div>
  );

  const AboutUploader = () => (
    <div className="mb-3">
      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
        <FaCamera size={14} className="text-muted" />
        About Image
      </Form.Label>
      
      <input
        ref={aboutInputRef}
        type="file"
        onChange={handleAboutSelect}
        style={{ display: "none" }}
        accept="image/*"
      />
      
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <Button
          variant="outline-primary"
          onClick={triggerAboutInput}
          disabled={aboutState.isLoading}
          className="d-flex align-items-center gap-2"
        >
          {aboutState.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <FaUpload /> {aboutState.preview ? 'Change Image' : 'Upload Image'}
            </>
          )}
        </Button>
        
        {aboutState.progress > 0 && (
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: '120px' }}>
              <ProgressBar 
                now={aboutState.progress} 
                label={`${aboutState.progress}%`}
                variant={aboutState.progress === 100 ? "success" : "primary"}
              />
            </div>
          </div>
        )}
        
        {aboutState.preview && (
          <div className="position-relative d-inline-block">
            <Image
              src={aboutState.preview}
              alt="About Preview"
              style={{
                width: '120px',
                height: '120px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '2px solid #dee2e6'
              }}
              thumbnail
            />
            <Button
              variant="danger"
              size="sm"
              className="position-absolute top-0 end-0 translate-middle"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleAboutRemove}
              disabled={aboutState.isLoading}
            >
              <FaTimes />
            </Button>
          </div>
        )}
        
        {aboutState.file && (
          <small className="text-muted">
            Selected: {aboutState.file.name}
          </small>
        )}
      </div>
      <Form.Text className="text-muted">
        Recommended: JPG or PNG, 800x600px, portrait or square
      </Form.Text>
    </div>
  );

  const InsightUploader = () => (
    <div className="mb-3">
      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
        <FaCamera size={14} className="text-muted" />
        Insight Image
      </Form.Label>
      
      <input
        ref={insightInputRef}
        type="file"
        onChange={handleInsightSelect}
        style={{ display: "none" }}
        accept="image/*"
      />
      
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <Button
          variant="outline-primary"
          onClick={triggerInsightInput}
          disabled={insightState.isLoading}
          className="d-flex align-items-center gap-2"
        >
          {insightState.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <FaUpload /> {insightState.preview ? 'Change Image' : 'Upload Image'}
            </>
          )}
        </Button>
        
        {insightState.progress > 0 && (
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: '120px' }}>
              <ProgressBar 
                now={insightState.progress} 
                label={`${insightState.progress}%`}
                variant={insightState.progress === 100 ? "success" : "primary"}
              />
            </div>
          </div>
        )}
        
        {insightState.preview && (
          <div className="position-relative d-inline-block">
            <Image
              src={insightState.preview}
              alt="Insight Preview"
              style={{
                width: '120px',
                height: '120px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '2px solid #dee2e6'
              }}
              thumbnail
            />
            <Button
              variant="danger"
              size="sm"
              className="position-absolute top-0 end-0 translate-middle"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleInsightRemove}
              disabled={insightState.isLoading}
            >
              <FaTimes />
            </Button>
          </div>
        )}
        
        {insightState.file && (
          <small className="text-muted">
            Selected: {insightState.file.name}
          </small>
        )}
      </div>
      <Form.Text className="text-muted">
        Recommended: JPG or PNG, 800x800px, square format
      </Form.Text>
    </div>
  );

  const CharacterCounter = ({ value, max, label }) => (
    <div className="d-flex justify-content-between align-items-center mt-1">
      <small className="text-muted">{label}</small>
      <Badge 
        bg={value.length > max ? "danger" : value.length > max * 0.8 ? "warning" : "success"}
        className="character-counter"
      >
        {value.length}/{max}
      </Badge>
    </div>
  );

  // Loading state
  if (getLoading) {
    return (
      <MotionDiv>
        <Title>Website Content</Title>
        <Container className="p-4">
          <Skeleton height={30} count={10} className="mb-3" />
        </Container>
      </MotionDiv>
    );
  }

  return (
    <MotionDiv>
      <Title>Website Content</Title>
      
      <Container className="p-4">
        {Object.keys(errors).length > 0 && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Please fix the following errors:</Alert.Heading>
            <ul className="mb-0">
              {Object.entries(errors).map(([key, error]) => (
                <li key={key}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        <Form onSubmit={submitHandler}>
          {/* Brand Identity Section */}
          <Card className="shadow-sm mb-4 border-0">
            <Card.Body>
              <SectionHeader
                icon={FaCamera}
                title="Brand Identity"
                subtitle="Upload your logo and brand assets"
                color="primary"
              />
              
              <LogoUploader />
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Meta Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Meta Title (60 characters max)"
                      value={formData.metaTitle}
                      maxLength={60}
                      onChange={(e) => handleChange('metaTitle', e.target.value)}
                      isInvalid={touched.metaTitle && errors.metaTitle}
                    />
                    {errors.metaTitle && (
                      <Form.Control.Feedback type="invalid">
                        {errors.metaTitle}
                      </Form.Control.Feedback>
                    )}
                    <CharacterCounter 
                      value={formData.metaTitle} 
                      max={60} 
                      label="For SEO optimization" 
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Meta Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter Meta Description (160 characters max)"
                      value={formData.metaDescription}
                      maxLength={160}
                      onChange={(e) => handleChange('metaDescription', e.target.value)}
                    />
                    <CharacterCounter 
                      value={formData.metaDescription} 
                      max={160} 
                      label="Appears in search results" 
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Home Page Section */}
          <Card className="shadow-sm mb-4 border-0">
            <Card.Body>
              <SectionHeader
                icon={FaHome}
                title="Homepage Content"
                subtitle="Configure your homepage banner and messaging"
                color="success"
              />
              
              <BannerUploader />
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Main Heading</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Home Heading"
                      value={formData.homeHeading}
                      onChange={(e) => handleChange('homeHeading', e.target.value)}
                      isInvalid={touched.homeHeading && errors.homeHeading}
                    />
                    {errors.homeHeading && (
                      <Form.Control.Feedback type="invalid">
                        {errors.homeHeading}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Tagline</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Home Tagline"
                      value={formData.homeTagline}
                      onChange={(e) => handleChange('homeTagline', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* About Us Section */}
          <Card className="shadow-sm mb-4 border-0">
            <Card.Body>
              <SectionHeader
                icon={FaUser}
                title="About Us Section"
                subtitle="Tell your story and showcase your platform"
                color="warning"
              />
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">About Description</Form.Label>
                <TextEditor
                  description={formData.aboutDescription}
                  setDescription={(desc) => handleChange('aboutDescription', desc)}
                />
              </Form.Group>
              
              <AboutUploader />
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">About Image Alt Text</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Alt Text for better accessibility (125 characters max)"
                  value={formData.aboutImageAltText}
                  maxLength={125}
                  onChange={(e) => handleChange('aboutImageAltText', e.target.value)}
                />
                <CharacterCounter 
                  value={formData.aboutImageAltText} 
                  max={125} 
                  label="For accessibility and SEO" 
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Insight Section */}
          <Card className="shadow-sm mb-4 border-0">
            <Card.Body>
              <SectionHeader
                icon={FaLightbulb}
                title="Insight Section"
                subtitle="Share your expertise and insights"
                color="danger"
              />
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Insights Heading</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Insight Heading"
                      value={formData.insightHeading}
                      onChange={(e) => handleChange('insightHeading', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Insights Subheading</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Insight Subheading"
                      value={formData.insightSubheading}
                      onChange={(e) => handleChange('insightSubheading', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <InsightUploader />
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Insight Image Alt Text</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Alt Text for better accessibility (125 characters max)"
                  value={formData.insightImageAltText}
                  maxLength={125}
                  onChange={(e) => handleChange('insightImageAltText', e.target.value)}
                />
                <CharacterCounter 
                  value={formData.insightImageAltText} 
                  max={125} 
                  label="For accessibility and SEO" 
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Social Links Section */}
          <Card className="shadow-sm mb-4 border-0">
            <Card.Body>
              <SectionHeader
                icon={FaLink}
                title="Social Media Links"
                subtitle="Connect your social media profiles"
                color="info"
              />
              
              <Row>
                {socialPlatforms.map((platform) => {
                  const Icon = platform.icon;
                  const value = formData.socialLinks[platform.key];
                  const error = errors[`social_${platform.key}`];
                  
                  return (
                    <Col md={6} key={platform.key} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                          <Icon style={{ color: platform.color }} />
                          {platform.label}
                        </Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <FaGlobe />
                          </span>
                          <Form.Control
                            type="url"
                            placeholder={platform.placeholder}
                            value={value}
                            onChange={(e) => handleSocialChange(platform.key, e.target.value)}
                            isInvalid={!!error}
                          />
                          {value && isValidUrl(value) && (
                            <span className="input-group-text text-success">
                              <FaCheck />
                            </span>
                          )}
                        </div>
                        {error && (
                          <Form.Text className="text-danger">
                            {error}
                          </Form.Text>
                        )}
                        {value && !isValidUrl(value) && (
                          <Form.Text className="text-warning">
                            Please enter a valid URL
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
              
              <Alert variant="info" className="mt-3">
                <FaLink className="me-2" />
                Tip: You can leave social media fields empty if you don't want to display them.
              </Alert>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <Card className="shadow-sm border-0">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(-1)}
                  className="px-4"
                >
                  Cancel
                </Button>
                
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      fetchContent(); // Reset to original content
                      setErrors({});
                      setTouched({});
                      toast.info("Form reset to original values");
                    }}
                    className="px-4"
                  >
                    Reset
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    className="px-4"
                    disabled={createLoading || 
                      logoState.isLoading || 
                      bannerState.isLoading || 
                      aboutState.isLoading || 
                      insightState.isLoading}
                  >
                    {createLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Form>
      </Container>
    </MotionDiv>
  );
}